// Serenity — the "stand your ground" stance. The less the ship moves, the
// more it gains: a smooth 0..1 meter fills while the ship is slow and drains
// while it is fast (never binary), with tiered stance perks:
//   Tier 1 (≥ 1/3): 2× pickup magnet radius — collect without moving.
//   Tier 2 (≥ 2/3): +25% weapon damage and fire rate — the DPS stance.
//   Tier 3 (= 1, "Anchor"): golden nova pulse every 4 s + 2× shield regen.
// Enemy convergence is the built-in risk that keeps idling honest; wave
// intermissions are the natural charge windows. Fully deterministic: the
// meter derives from player velocity and the sim tick only.

import type { WorldState } from './state.js';
import type { Enemy } from '../types.js';
import { wrappedDelta } from './collision.js';
import { killEnemy } from './kill.js';
import { getUpgradeMods } from './upgrade-system.js';

/** Player speed that reads as "full movement" for drain purposes. */
const NORM_SPEED = 8;
/** Full stillness fills 0→1 in 4 s (240 ticks). */
const FILL_PER_TICK = 1 / 240;
/** Drain at full speed: a full meter is lost in ~1.5 s. Fill and drain
 * balance at ≈2.2 u/s — slower than that charges, faster drains. */
const DRAIN_PER_TICK = 1 / 90;

export const SERENITY_TIER1 = 1 / 3;
export const SERENITY_TIER2 = 2 / 3;

/** Anchor nova pulse (tier 3). */
export const SERENITY_NOVA_INTERVAL_TICKS = 240;
export const SERENITY_NOVA_RADIUS = 6;
export const SERENITY_NOVA_DAMAGE = 8;

interface SerenityState {
  seed: number;
  /** 0..1 meter. */
  value: number;
  lastNovaTick: number;
  /** Anchor window: ignites when the meter first fills. While it lasts and
   * the ship stays still, tier 3 holds; once the window expires (or the ship
   * moves early), the burn begins — the meter is capped at tier 1 until the
   * ship travels RELOCATE_DISTANCE. Idling is a window, never a home. */
  burning: boolean;
  anchorLeft: number;
  travelSinceBurn: number;
}

const serenityStates = new WeakMap<WorldState, SerenityState>();

/** Anchor window length: 8 s of tier-3 uptime per full charge. */
const ANCHOR_WINDOW_TICKS = 480;
/** While burning, the meter cannot climb above tier 1. */
const BURN_CAP = SERENITY_TIER1;
/** Distance the ship must travel to clear the burn and charge again. */
const RELOCATE_DISTANCE = 8;

function getState(world: WorldState): SerenityState {
  let s = serenityStates.get(world);
  // A new run (seed change) restarts the meter.
  if (!s || s.seed !== world.runSeed) {
    s = {
      seed: world.runSeed,
      value: 0,
      lastNovaTick: -SERENITY_NOVA_INTERVAL_TICKS,
      burning: false,
      anchorLeft: 0,
      travelSinceBurn: 0,
    };
    serenityStates.set(world, s);
  }
  return s;
}

export interface SerenityMods {
  /** 0..1 meter value (drives the HUD bar). */
  value: number;
  tier: 0 | 1 | 2 | 3;
  magnetMult: number;
  damageMult: number;
  rateMult: number;
  shieldRegenMult: number;
  /** Burn state: meter capped at tier 1 until the ship relocates (HUD hint). */
  burning: boolean;
}

/** Stance perks for the current meter value. Pure read. */
export function getSerenityMods(world: WorldState): SerenityMods {
  const s = getState(world);
  const v = s.value;
  const tier: 0 | 1 | 2 | 3 = v >= 1 ? 3 : v >= SERENITY_TIER2 ? 2 : v >= SERENITY_TIER1 ? 1 : 0;
  // Stillpoint Gate boon: while the Anchor holds (tier 3), each rank adds its
  // damage bonus on top of the tier-2 stance perk (multiplicative: a full
  // meter with one rank reads 1.25 × 1.20 = 1.50).
  const gateMult =
    tier >= 3 ? 1 + getUpgradeMods(world.player).serenityDamageMult : 1;
  return {
    value: v,
    tier,
    magnetMult: tier >= 1 ? 2 : 1,
    damageMult: (tier >= 2 ? 1.25 : 1) * gateMult,
    rateMult: tier >= 2 ? 1.25 : 1,
    shieldRegenMult: tier >= 3 ? 2 : 1,
    burning: s.burning && s.anchorLeft <= 0,
  };
}

/**
 * Per-tick update: fill/drain the meter from the current velocity, then fire
 * the Anchor nova when due. Call once per sim step after player movement so
 * this tick's speed is what shapes the meter.
 */
export function updateSerenity(world: WorldState, tick: number): void {
  const s = getState(world);
  if (!world.player.alive) {
    s.value = 0;
    return;
  }

  const v = world.player.vel;
  const speed = Math.hypot(v.x, v.y);
  const norm = Math.min(1, speed / NORM_SPEED);

  // Anchor state machine: ignite at full meter, hold while still, then burn
  // out to the tier-1 cap until the ship relocates.
  if (!s.burning && s.value >= 1) {
    s.burning = true;
    s.anchorLeft = ANCHOR_WINDOW_TICKS;
    s.travelSinceBurn = 0;
  }
  if (s.burning) {
    s.travelSinceBurn += speed / 60;
    if (s.travelSinceBurn >= RELOCATE_DISTANCE) {
      // Relocated: the burn clears and the meter may charge again.
      s.burning = false;
      s.anchorLeft = 0;
      s.travelSinceBurn = 0;
    } else if (s.anchorLeft > 0 && speed < 0.5) {
      s.anchorLeft--;
      s.value = 1; // anchor holds while the window lasts and the ship is still
    } else {
      // Window expired (or the ship moved early): capped at tier 1.
      s.anchorLeft = Math.max(0, s.anchorLeft - 1);
      s.value = Math.min(1, Math.max(0, s.value + FILL_PER_TICK * (1 - norm) - DRAIN_PER_TICK * norm));
      s.value = Math.min(s.value, BURN_CAP);
    }
  } else {
    s.value = Math.min(1, Math.max(0, s.value + FILL_PER_TICK * (1 - norm) - DRAIN_PER_TICK * norm));
  }

  if (s.value < 1 || tick - s.lastNovaTick < SERENITY_NOVA_INTERVAL_TICKS) return;
  s.lastNovaTick = tick;

  // Collect first: killEnemy releases pool slots, which would disturb the
  // live iterator (same pattern as the weapon system's hit resolution).
  const targets: Enemy[] = [];
  for (const e of world.enemies.alive()) {
    if (!e.targetable || tick < e.telegraph_until) continue;
    const d = wrappedDelta(e.pos, world.player.pos);
    if (d.x * d.x + d.y * d.y > SERENITY_NOVA_RADIUS * SERENITY_NOVA_RADIUS) continue;
    targets.push(e);
  }
  for (const e of targets) {
    e.hp -= SERENITY_NOVA_DAMAGE;
    if (e.hp <= 0) killEnemy(world, e, tick);
  }

  world.events.emit(
    'nova',
    {
      pos: { x: world.player.pos.x, y: world.player.pos.y },
      radius: SERENITY_NOVA_RADIUS,
      hits: targets.length,
    },
    tick
  );
}
